<template>
  <Page>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="title">ผังเดือน</div>
          </div>
          <div class="actions">
            <router-link
              :to="'/schedules-monthly/create'"
              class="btn btn-primary">
              <i class="fas fa-plus"></i>
              สร้างผังเดือน
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <PreloaderSection v-if="loadSchedulesState.isLoading" />
      <div v-if="!loadSchedulesState.isLoading">
        <Section v-if="!loadSchedulesState.error">
          <template v-slot:section-filter>
            <input
              class="form-control form-control-search"
              placeholder="ค้นหาด้วยชื่อผัง..."
              @change="onSearch"/>
            <Dropdown :alignment="'right'" class="search-dropdown ml-2">
              <template v-slot:dropdow-button>
                <button type="button" class="btn btn-action m-0">
                  <i class="fa fa-chevron-down" />
                </button>
              </template>
              <div class="dropdown-item">
                <div>สถานะ</div>
                <select class="form-control" @change="onSelectedStatus">
                  <option value="any">ทั้งหมด</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>
            </Dropdown>
          </template>
          <template v-slot:section-body>
            <div v-if="!isEmpty()">
              <List>
                <template v-slot:list-header>
                  <div class="list-item-detail">
                    <div class="list-item list-item-primary list-item-title">
                      ชื่อผัง
                    </div>
                    <div class="list-item">สร้างเมื่อ</div>
                    <div class="list-item">สถานะ</div>
                    <div class="list-item list-item-action"></div>
                  </div>
                </template>
                <template v-slot:list-body>
                  <div
                    class="list-item-container no-underline"
                    v-for="(item, itemIndex) in schedules"
                    :key="item.id"
                    @click.stop="goToPageDetail(`/schedules-monthly/${item.id}`, itemIndex, $event)">
                    <div class="list-item-detail">
                      <div
                        v-if="deleteScheduleState.isDeleting === item.id"
                        class="list-item list-item-primary list-item-title">
                        กำลังลบผังเดือน
                      </div>
                      <div
                        v-if="deleteScheduleState.isDeleting !== item.id"
                        class="list-item list-item-primary list-item-title">
                        {{ item.name }}
                      </div>
                      <div class="list-item">
                        {{ formatDateTime(item.createdAt) }}
                      </div>
                      <div class="list-item list-item-status">
                        <div class="badge badge-status" :class="item.status">
                          {{ item.status }}
                        </div>
                      </div>
                      <div class="list-item list-item-action">
                        <div class="action-container">
                          <DoubleTabToConfirmButton
                            :disabled="deleteScheduleState.isDeleting === item.id"
                            @confirm="onDelete(item.id)"/>
                          <router-link
                            class="btn btn-light btn-action"
                            :to="`/schedules-monthly/${item.id}`">
                            <i class="fas fa-pencil-alt"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </List>
              <div class="pagination-container" v-if="isShowPagination()">
                <Pagination :lastPage="lastPage"/>
              </div>
            </div>
            <!-- empty list --->
            <EmptyMessage v-if="isEmpty()">
              <h3>ไม่มีข้อมูล</h3>
              <router-link
                class="btn btn-light btn-lg mt-4"
                :to="`/schedules-monthly/create`">
                สร้างผังเดือน
              </router-link>
            </EmptyMessage>
            <!-- search result -->
            <EmptyMessage v-if="isEmptySearch()">
              <h3>ไม่พบผังเดือนที่ค้นหา</h3>
              <button
                type="button"
                class="btn btn-light btn-lg mt-4"
                @click="clearSearch">
                ล้างการค้นหา
              </button>
            </EmptyMessage>
          </template>
        </Section>
        <!-- error from api -->
        <ErrorMessageMonthlyScheduleContainer
          v-if="loadSchedulesState.error"
          :error="loadSchedulesState.error"/>
      </div>
    </div>
  </Page>
</template>

<script>
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import Page from '@/layouts/Page.vue';
import Dropdown from '@/components/Dropdown.vue';
import DoubleTabToConfirmButton from '@/components/DoubleTapToConfirmButton.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import List from '@/components/List.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';
import Pagination from '@/components/Pagination.vue';
import Section from '@/components/Section.vue';
import {
  loadScheduleMonthly,
  deleteMonthlySchedule,
} from './api';
import ErrorMessageMonthlyScheduleContainer from './ErrorMessageMonthlyScheduleContainer.vue';

export default {
  mixins: [dateFormetter],
  components: {
    Page,
    Section,
    List,
    PreloaderSection,
    Pagination,
    EmptyMessage,
    Dropdown,
    ErrorMessageMonthlyScheduleContainer,
    DoubleTabToConfirmButton,
  },
  data() {
    return {
      schedules: null,
      lastPage: null,
      loadSchedulesState: {
        isLoading: true,
        error: null,
      },
      deleteScheduleState: {
        isDeleting: false,
        error: null,
      },
      selectedStatus: 'all',
      modal: false,
    };
  },
  computed: {
    filter() {
      return this.$route.query;
    },
  },
  watch: {
    filter(newValue) {
      this.load(newValue);
    },
  },
  created() {
    this.load(this.filter);
  },
  methods: {
    async load(filter) {
      try {
        const response = await loadScheduleMonthly(filter);
        this.schedules = response.data.data;
        this.lastPage = response.data.lastPage;
        this.loadSchedulesState.isLoading = false;
      } catch (error) {
        this.loadSchedulesState.error = error.response;
        this.loadSchedulesState.isLoading = false;
      }
    },
    handleModal() {
      this.modal = !this.modal;
    },
    isEmptySearch() {
      return _.isEmpty(this.schedules) && !_.every(this.filter, _.isEmpty);
    },
    isEmpty() {
      return _.isEmpty(this.schedules) && _.every(this.filter, _.isEmpty);
    },
    onSearch(event) {
      let filter;
      if (event.target.value) {
        filter = { ...this.filter, q: event.target.value };
      } else {
        filter = { ...this.filter };
        delete filter.q;
      }
      this.$router.push({ query: { ...filter } });
    },
    clearSearch() {
      const filter = null;
      this.$router.push({ query: { ...filter } });
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
    onSelectedStatus(event) {
      const status = event.target.value;
      const filter = { ...this.filter, status };
      this.$router.push({ query: { ...filter } });
    },
    async onDelete(id) {
      try {
        this.deleteScheduleState.isDeleting = id;
        await deleteMonthlySchedule(id);
        await this.load(this.filter);
        this.deleteScheduleState.isDeleting = false;
      } catch (error) {
        this.deleteScheduleState.error = error.response;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/form.scss';

</style>
